import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import classnames from 'classnames'
import { getTenants, getPropertyList, getUtilityList } from './Tenants.slice'
import Layout from './Layout'
import Table from '../../../components/Table/Table'
import QuickSearch from '../../../components/QuickSearch/QuickSearch'
import ComboboxAutocomplete from '../../../components/Form/ComboboxAutocomplete'
import Panel from '../../../components/Panel'
import Badge from '../../../components/Badge'
import PropertyDetailPanels from '../../properties/PropertyDetailPanels'
import { formatCurrency } from '../../../util/string'
import qs from 'qs'
import { useAppDispatch, useAppSelector } from '../../../shared/redux/hooks'
import { setNotesViewContext } from '../../notes/notesViewContext'
import type { TenantAccountListItem } from '../../../apis/types'
import {
  getArBillingStanding,
  getArBillingStandingBadgeLabel,
  getBillingModeChip,
  getHeaderBalanceAmounts,
  HEADER_BALANCE_CHIP_TOOLTIPS,
  getKeAccountStatus,
  getOptiRateStatus,
  getUtilityAccountStatus,
  getVnemStatus,
  type ServiceAccountWithInvoices,
} from './components/accountStatusUtils'
import {
  areAllTenantsExpanded,
  flattenTenantListRows,
  tenantListRowHref,
  type TenantListRow,
} from './tenantListRows'

const EXPAND_ALL_BUTTON =
  'text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300'

const amountTone = (amount: number): string =>
  amount > 0 ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'

const ListBadge: React.FC<React.ComponentProps<typeof Badge>> = (props) => (
  <Badge variant="soft" truncate={false} {...props} />
)

const badgeCol = { contentClassName: '!min-w-max' } as const

/** List light accounts already carry standing fields the helpers prefer. */
const asStandingAccount = (account: TenantAccountListItem): ServiceAccountWithInvoices =>
  account as unknown as ServiceAccountWithInvoices

const Tenants: React.FC = () => {
  const { search } = useLocation()
  const searchParams = new URLSearchParams(search)
  const propertyId = searchParams.get('propertyId')
  const utilityId = searchParams.get('utilityId')
  const [quickSearchTerm, setQuickSearchTerm] = useState<string | undefined>()
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set())
  const [listLoading, setListLoading] = useState(true)
  const listLoadGeneration = useRef(0)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { tenants, properties, utilities } = useAppSelector((state) => state.tenants)

  const loadTenants = useCallback(
    (page: number) => {
      const generation = ++listLoadGeneration.current
      setListLoading(true)
      void dispatch(
        getTenants({
          page,
          resultsPerPage: 20,
          propertyId: propertyId?.toString(),
          utilityId: utilityId?.toString(),
          tenantName: quickSearchTerm,
        })
      ).finally(() => {
        if (generation === listLoadGeneration.current) {
          setListLoading(false)
        }
      })
    },
    [dispatch, propertyId, utilityId, quickSearchTerm]
  )

  useEffect(() => {
    properties?.length === 0 && dispatch(getPropertyList())
    utilities?.length === 0 && dispatch(getUtilityList())
    loadTenants(0)
  }, [loadTenants, dispatch])

  // Collapse expansions when filters or page change (not on expand toggles).
  useEffect(() => {
    setExpandedIds(new Set())
  }, [tenants?.page, propertyId, utilityId, quickSearchTerm])

  const tenantResults = tenants?.results ?? []
  const allExpanded = areAllTenantsExpanded(tenantResults, expandedIds)

  const toggleTenant = useCallback((tenantId: string): void => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(tenantId)) next.delete(tenantId)
      else next.add(tenantId)
      return next
    })
  }, [])

  const toggleExpandAll = useCallback((): void => {
    setExpandedIds((prev) => {
      if (areAllTenantsExpanded(tenantResults, prev)) return new Set()
      return new Set(tenantResults.map((t) => t.id))
    })
  }, [tenantResults])

  const handlePaginatorClick = (page: number): void => {
    loadTenants(page)
  }

  const handleQuickSearchChange = (value: string): void => {
    if (value === '') {
      setQuickSearchTerm(undefined)
      return
    }
    setQuickSearchTerm(value)
  }

  const handleSelectProperty = (propertyId: string | undefined): void => {
    navigate(
      {
        search: qs.stringify({ propertyId }, { addQueryPrefix: true, skipNulls: true }),
      },
      { replace: true }
    )
  }

  const handleSelectUtility = (utilityId: string | undefined): void => {
    navigate(
      {
        search: qs.stringify({ utilityId }, { addQueryPrefix: true, skipNulls: true }),
      },
      { replace: true }
    )
  }

  const flatRows = useMemo(
    () => flattenTenantListRows(tenantResults, expandedIds),
    [tenantResults, expandedIds]
  )

  const selectedProperty = properties?.find((entry) => entry.id === propertyId)

  const tableData = useMemo(() => {
    if (tenants == null) {
      return {
        results: flatRows,
        total: 0,
        page: 0,
        resultsPerPage: 20,
      }
    }
    return {
      ...tenants,
      results: flatRows,
    }
  }, [tenants, flatRows])

  useEffect(() => {
    if (!propertyId) {
      setNotesViewContext(null)
      return
    }
    const property = properties?.find((entry) => entry.id === propertyId)
    if (!property) {
      return
    }
    setNotesViewContext({
      sourceEntity: `property|${propertyId}`,
      label: property.name,
    })
    return () => setNotesViewContext(null)
  }, [propertyId, properties])

  return (
    <>
      <Layout title="Tenants">
        <Panel header="Filters" className="mb-5">
          <div className="grid grid-auto-flow grid-cols-3 gap-3">
            <QuickSearch
              title="Tenant name"
              initialValue={quickSearchTerm}
              handleChange={handleQuickSearchChange}
            />
            <div>
              <label
                htmlFor="propertyNames"
                className="block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                Property
              </label>
              {properties !== undefined && (
                <ComboboxAutocomplete
                  labelPlural="Properties"
                  value={propertyId ?? ''}
                  handleChange={handleSelectProperty}
                  data={properties?.map((property: { id: string; nameLocation: string }) => ({
                    id: property.id,
                    name: property.nameLocation,
                  }))}
                />
              )}
            </div>
            <div>
              <label
                htmlFor="utilityNames"
                className="block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                Utility
              </label>
              {utilities !== undefined && (
                <ComboboxAutocomplete
                  labelPlural="Utilities"
                  value={utilityId ?? ''}
                  handleChange={handleSelectUtility}
                  data={utilities?.map((utility: { id: string; name: string }) => ({
                    id: utility.id,
                    name: utility.name,
                  }))}
                />
              )}
            </div>
          </div>
        </Panel>

        <>
            {tenantResults.length > 0 && (
              <div className="mb-3 flex justify-end">
                <button type="button" onClick={toggleExpandAll} className={EXPAND_ALL_BUTTON}>
                  {allExpanded ? 'Collapse all' : 'Expand all'}
                </button>
              </div>
            )}
            <Table<TenantListRow>
              isLoading={listLoading}
              scrollShell={{
                // Sticky headers only work inside a height-capped scrollport.
                // Boolean `scrollShell` has no max-height, so the page scrolls instead
                // and the header rides away with the rows.
                heightClassName: 'max-h-[calc(100dvh-22rem)]',
              }}
              stickyHeader
              compact
              data={tableData}
              getRowHref={tenantListRowHref}
              handlePaginatorClick={handlePaginatorClick}
              rowClassName={(row) =>
                row.kind === 'account'
                  ? '!bg-indigo-50 dark:!bg-indigo-950 group-hover:!bg-indigo-100 dark:group-hover:!bg-indigo-900'
                  : ''
              }
              colConfig={[
                {
                  label: '',
                  isFrozen: true,
                  frozenMinWidth: 36,
                  skipRowLink: true,
                  cellClassName: '!p-0',
                  contentClassName: 'flex h-full min-h-[1.75rem] items-center justify-center',
                  render: (row) => {
                    if (row.kind !== 'tenant') {
                      return <span className="inline-block w-3" aria-hidden />
                    }
                    const accountCount = row.tenant.serviceAccounts?.length ?? 0
                    if (accountCount === 0) {
                      return <span className="inline-block w-3" aria-hidden />
                    }
                    const open = expandedIds.has(row.tenant.id)
                    return (
                      <button
                        type="button"
                        aria-expanded={open}
                        aria-label={open ? 'Collapse accounts' : 'Expand accounts'}
                        className="inline-flex h-7 w-full items-center justify-center rounded text-slate-500 hover:bg-slate-200/80 dark:text-slate-300 dark:hover:bg-slate-700"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          toggleTenant(row.tenant.id)
                        }}
                      >
                        <span
                          aria-hidden
                          className={classnames(
                            'inline-block text-xs leading-none transition-transform duration-150',
                            open && 'rotate-90'
                          )}
                        >
                          ▸
                        </span>
                      </button>
                    )
                  },
                },
                {
                  label: 'Tenant / Account',
                  isFrozen: true,
                  frozenMinWidth: 220,
                  dividerAfter: true,
                  render: (row) => {
                    if (row.kind === 'tenant') {
                      const count = row.tenant.serviceAccounts?.length ?? 0
                      return (
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="truncate font-medium">
                            {row.tenant.name.split('-')?.[0]}
                          </span>
                          {count > 0 && (
                            <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[10px] font-medium leading-none text-indigo-700 dark:bg-indigo-500/30 dark:text-indigo-200">
                              {count}
                            </span>
                          )}
                        </div>
                      )
                    }
                    return (
                      <div className="min-w-0 pl-4">
                        <div className="truncate font-medium text-slate-800 dark:text-slate-100">
                          {row.account?.kingAccountName}
                        </div>
                        <div className="font-mono text-xs text-slate-500 dark:text-slate-400">
                          {row.account?.kingAccountNumber}
                        </div>
                      </div>
                    )
                  },
                },
                {
                  label: 'Property',
                  columnMinWidth: 180,
                  render: (row) => {
                    if (row.kind !== 'tenant') return null
                    const name = row.tenant.property?.name
                    const location = row.tenant.property?.location
                    if (name == null && location == null) return null
                    return (
                      <div className="min-w-0">
                        {name != null && <div className="truncate">{name}</div>}
                        {location != null && location !== '' && (
                          <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                            {location}
                          </div>
                        )}
                      </div>
                    )
                  },
                },
                {
                  label: 'Utility',
                  columnMinWidth: 88,
                  ...badgeCol,
                  render: (row) => {
                    if (row.kind !== 'tenant') return null
                    const utility = row.tenant.property?.utilityShortName
                    if (utility == null || utility === '') return null
                    return <ListBadge status={utility} displayLabel={utility} />
                  },
                },
                {
                  label: 'Balance Total',
                  align: 'right',
                  columnWidth: 110,
                  render: (row) => {
                    if (row.kind !== 'account' || row.account == null) return null
                    const { balanceTotal } = getHeaderBalanceAmounts(asStandingAccount(row.account))
                    return (
                      <span className={classnames('text-xs font-medium', amountTone(balanceTotal))}>
                        {formatCurrency(balanceTotal)}
                      </span>
                    )
                  },
                },
                {
                  label: 'KE Balance Total',
                  align: 'right',
                  columnWidth: 120,
                  render: (row) => {
                    if (row.kind !== 'account' || row.account == null) return null
                    const { keBalanceTotal } = getHeaderBalanceAmounts(asStandingAccount(row.account))
                    return (
                      <span className={classnames('text-xs font-medium', amountTone(keBalanceTotal))}>
                        {formatCurrency(keBalanceTotal)}
                      </span>
                    )
                  },
                },
                {
                  label: 'Balance Due',
                  align: 'right',
                  columnWidth: 100,
                  render: (row) => {
                    if (row.kind !== 'account' || row.account == null) return null
                    const { balanceDue } = getHeaderBalanceAmounts(asStandingAccount(row.account))
                    return (
                      <span
                        title={HEADER_BALANCE_CHIP_TOOLTIPS.balanceDue}
                        className={classnames('text-xs font-medium', amountTone(balanceDue))}
                      >
                        {formatCurrency(balanceDue)}
                      </span>
                    )
                  },
                },
                {
                  label: 'KE Account',
                  columnMinWidth: 128,
                  ...badgeCol,
                  render: (row) => {
                    if (row.kind !== 'account' || row.account == null) return null
                    const status = getKeAccountStatus(row.account.accountStatus, asStandingAccount(row.account))
                    return <ListBadge status={status} />
                  },
                },
                {
                  label: 'Utility Account',
                  columnMinWidth: 120,
                  ...badgeCol,
                  render: (row) => {
                    if (row.kind !== 'account' || row.account == null) return null
                    return (
                      <ListBadge status={getUtilityAccountStatus(asStandingAccount(row.account))} />
                    )
                  },
                },
                {
                  label: 'Billing Standing',
                  columnMinWidth: 180,
                  ...badgeCol,
                  render: (row) => {
                    if (row.kind !== 'account' || row.account == null) return null
                    const standing = getArBillingStanding(asStandingAccount(row.account))
                    return (
                      <ListBadge
                        status={standing}
                        displayLabel={getArBillingStandingBadgeLabel(
                          standing,
                          row.account.daysPastDue
                        )}
                      />
                    )
                  },
                },
                {
                  label: 'VNEM Status',
                  columnMinWidth: 120,
                  ...badgeCol,
                  render: (row) => {
                    if (row.kind !== 'account' || row.account == null) return null
                    return (
                      <ListBadge status={getVnemStatus(undefined, asStandingAccount(row.account))} />
                    )
                  },
                },
                {
                  label: 'Billing Mode',
                  columnMinWidth: 160,
                  ...badgeCol,
                  render: (row) => {
                    if (row.kind !== 'account' || row.account == null) return null
                    const chip = getBillingModeChip(asStandingAccount(row.account))
                    return (
                      <ListBadge status={chip.status} displayLabel={chip.displayLabel} />
                    )
                  },
                },
                {
                  label: 'Paperless',
                  columnMinWidth: 88,
                  ...badgeCol,
                  render: (row) => {
                    if (row.kind !== 'account' || row.account == null) return null
                    return (
                      <ListBadge
                        status={row.account.mailingPreference.paperless ? 'ENABLED' : 'DISABLED'}
                      />
                    )
                  },
                },
                {
                  label: 'AutoPay',
                  columnMinWidth: 88,
                  ...badgeCol,
                  render: (row) => {
                    if (row.kind !== 'account' || row.account == null) return null
                    return (
                      <ListBadge
                        status={
                          row.account.paymentPreference.autopay.enabled ? 'ENABLED' : 'DISABLED'
                        }
                      />
                    )
                  },
                },
                {
                  label: 'OptiRate',
                  columnMinWidth: 120,
                  ...badgeCol,
                  render: (row) => {
                    if (row.kind !== 'account' || row.account == null) return null
                    return (
                      <ListBadge status={getOptiRateStatus(asStandingAccount(row.account))} />
                    )
                  },
                },
              ]}
            />
        </>

        {propertyId != null && (
          <div className="mt-6">
            <PropertyDetailPanels propertyId={propertyId} propertyName={selectedProperty?.nameLocation} />
          </div>
        )}
      </Layout>
    </>
  )
}

export default Tenants
